/**
 * List-view config for the Workflows page: the row type, the display helpers (schedule / destination /
 * last-run labels), and the search/sort/filter wiring the shared list-page pattern consumes
 * (`useListControls` + `filterSortItems` + `ListToolbar`).
 */
import type { WorkflowGraph, WorkflowStatus, WorkflowTrigger } from '@server/types';
import type { AccountRepairListState } from '@server/warehouse-repair-state';
import dayjs from 'dayjs';

import type { FilterGroup, SortOption } from '@/_shared/components/list/ListToolbar';
import { timeAgo } from '@/_shared/lib/format';
import type { ListDeriveConfig, SortState } from '@/_shared/lib/list-controls';

import { triggerToSchedule } from '../mappers';
import { scheduleLabel } from './schedule-label';

/** The subset of a workflow-list row the list view renders. Assignable from the `workflow.list` item. */
export interface WorkflowListItem {
  id: string;
  name: string;
  description: string;
  status: WorkflowStatus;
  trigger: WorkflowTrigger;
  graph: WorkflowGraph;
  updatedAt: string;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  /** ACT2-1286: the worst query step's needs-repair state and the repair pause (`ok` with the flag off). */
  accountRepair?: AccountRepairListState & { repairPaused: boolean };
}

export type WorkflowSortKey = 'updated' | 'name' | 'lastRun' | 'status';
export type WorkflowFilterKey = 'status';

// ── Display helpers (moved out of the view; shared by the card + table renderers) ──

/**
 * Human-readable schedule label from a persisted trigger, e.g. "Daily at 09:00 (UTC)" — the same phrase
 * the editor's trigger block renders (it prefixes "Runs "), so the two surfaces can't drift.
 */
export function describeTrigger(trigger: { type: string; cron?: string; timezone?: string }): string {
  return scheduleLabel(triggerToSchedule(trigger));
}

/**
 * The Slack node's bound channel id (the graph stores the id, not the #name). Typed against
 * `WorkflowGraph` so a schema rename breaks the build here instead of silently dropping the channel.
 * The optional chaining is deliberate despite that type: a persisted graph is cast out of a JSON
 * column, never validated, so `nodes` and a node's `config` can both be missing at runtime.
 */
export function slackChannelId(graph: WorkflowGraph): string | null {
  const slack = (graph?.nodes ?? []).find((n) => n.kind === 'slack');
  return slack?.config?.channel ?? null;
}

/** Display label for a resolved Slack channel — strips a leading `#`, falling back to a plain "Slack". */
export function slackChannelLabel(channel: string | null): string {
  return channel ? channel.replace(/^#/, '') : 'Slack';
}

/**
 * "Last run …" label from the list-only `lastRunAt`/`lastRunStatus`, coloured by outcome
 * (completed → success, failed → danger); "Never run" for routines with no finished run.
 */
export function describeLastRun(
  lastRunAt: string | null,
  lastRunStatus: string | null,
): { text: string; tone: string } {
  if (!lastRunAt) return { text: 'Never run', tone: 'text-muted-foreground' };
  // Guard a malformed timestamp — the shared `timeAgo` (dayjs.fromNow) would otherwise render "Invalid Date".
  const rel = dayjs(lastRunAt).isValid() ? timeAgo(lastRunAt) : 'recently';
  if (lastRunStatus === 'completed') return { text: `Last run ${rel} · success`, tone: 'text-success' };
  if (lastRunStatus === 'failed') return { text: `Last run ${rel} · failed`, tone: 'text-danger' };
  return { text: `Last run ${rel}${lastRunStatus ? ` · ${lastRunStatus}` : ''}`, tone: 'text-muted-foreground' };
}

// ── Toolbar options + derive config ──

export const WORKFLOW_SORT_KEYS: readonly WorkflowSortKey[] = ['updated', 'name', 'lastRun', 'status'];

export const WORKFLOW_DEFAULT_SORT: SortState<WorkflowSortKey> = { key: 'updated', dir: 'desc' };

export const WORKFLOW_SORT_OPTIONS: readonly SortOption[] = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'name', label: 'Name' },
  { value: 'lastRun', label: 'Last run' },
  { value: 'status', label: 'Status' },
];

export const WORKFLOW_STATUS_OPTIONS: readonly { value: WorkflowStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
];

/** Grouped filters for the toolbar (one selection per group). Status today; extensible. */
export const WORKFLOW_FILTER_GROUPS: readonly FilterGroup[] = [
  { key: 'status', label: 'Status', options: WORKFLOW_STATUS_OPTIONS },
];

/** Stable filter-id set for `useListControls` (a module const so the caller doesn't pass a fresh array each render). */
export const WORKFLOW_FILTER_KEYS: readonly WorkflowFilterKey[] = ['status'];

/** Allowed values per filter, so `useListControls` can reject bogus URL params (e.g. `?f_status=nope`). */
export const WORKFLOW_FILTER_VALUES: Partial<Record<WorkflowFilterKey, readonly string[]>> = {
  status: WORKFLOW_STATUS_OPTIONS.map((o) => o.value),
};

const STATUS_ORDER: Record<WorkflowStatus, number> = { active: 0, paused: 1, draft: 2 };

const byString = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Build the filter/sort/search config. Search covers the name, description, and the resolved Slack
 * channel label (so "#growth" finds routines posting there); the resolver is page state, hence a factory.
 */
export function makeWorkflowDeriveConfig(
  resolveChannel: (channelId: string | null) => string | null,
): ListDeriveConfig<WorkflowListItem, WorkflowSortKey, WorkflowFilterKey> {
  return {
    searchText: (wf) => [wf.name, wf.description, resolveChannel(slackChannelId(wf.graph)) ?? ''].join(' '),
    comparators: {
      name: (a, b) => a.name.localeCompare(b.name),
      updated: (a, b) => byString(a.updatedAt, b.updatedAt),
      lastRun: (a, b) => byString(a.lastRunAt ?? '', b.lastRunAt ?? ''),
      status: (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
    },
    filterPredicates: {
      status: (wf, value) => wf.status === value,
    },
  };
}
